import {
  cn,
  createDataTableColumnHelper,
  DataTable,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@beweegbaas/ui";
import { createFileRoute } from "@tanstack/react-router";
import { ErrorBoundaryComponent } from "#/components/error-boundary";
import { useRouteOverlayOpen } from "#/hooks/use-route-overlay-open";
import { getLessonAdministrationLeerlingTimeouts } from "#/modules/lesadministratie.statistics/lesson-administration.functions";
import { formatDate } from "#/modules/util.helpers/format";
import { m } from "#/paraglide/messages";
import type { components } from "#/schema";

type LeerlingTimeout = components["schemas"]["LeerlingTimeoutModel"];

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/insights/lesson-administration/timeouts/$leerlingId",
)({
  // No search params of its own — `klasIds`/`from`/`to` are inherited from the parent
  // `lesson-administration` route's `validateSearch`. Narrowed to exactly the keys the loader uses.
  loaderDeps: ({ search }) => ({ klasIds: search.klasIds, from: search.from, to: search.to }),
  loader: ({ deps, params }) =>
    getLessonAdministrationLeerlingTimeouts({
      data: {
        filters: deps,
        leerlingId: params.leerlingId,
        locale: params.locale,
        school: params.school,
        schoolyear: params.schoolyear,
      },
    }),
  component: LessonAdministrationTimeoutsPage,
  errorComponent: LessonAdministrationTimeoutsError,
});

function timeoutTypeLabel(type: LeerlingTimeout["type"]): string {
  switch (type) {
    case "KorteTimeout":
      return m["common.labels.korte_timeout"]();
    case "LangeTimeout":
      return m["common.labels.lange_timeout"]();
    case "UitsluitingLes":
      return m["common.labels.uitsluiting_les"]();
    default:
      return "–";
  }
}

const helper = createDataTableColumnHelper<LeerlingTimeout>();

const columns = helper.columns([
  helper.accessor("datum", {
    header: () => m["common.fields.date"](),
    meta: { cellClassName: "font-medium tabular-nums" },
    cell: ({ getValue }) => formatDate(getValue()),
  }),
  helper.accessor("klasNamen", {
    header: () => m["common.fields.klas"](),
    meta: { cellClassName: "text-muted-foreground text-sm" },
  }),
  helper.accessor("type", {
    header: () => m["lesadministratie.timeouts_col_type"](),
    meta: { cellClassName: "text-muted-foreground text-sm" },
    cell: ({ getValue }) => timeoutTypeLabel(getValue()),
  }),
]);

function LessonAdministrationTimeoutsPage() {
  const { locale, schoolyear, school } = Route.useParams();
  const loaderData = Route.useLoaderData();
  const navigate = Route.useNavigate();

  const dialog = useRouteOverlayOpen(() => {
    void navigate({
      to: "/$locale/{-$schoolyear}/$school/insights/lesson-administration",
      params: { locale, schoolyear, school },
      search: (prev) => ({ ...prev }),
      replace: true,
      resetScroll: false,
      viewTransition: true,
    });
  });

  return (
    <Dialog {...dialog}>
      <DialogContent
        className={cn({
          "md:max-w-lg!": loaderData.data.timeouts.length === 0,
        })}
        showCloseButton
      >
        <DialogHeader>
          <DialogTitle>{loaderData.data.naam}</DialogTitle>
          <DialogDescription>{m["lesadministratie.timeouts_description"]()}</DialogDescription>
        </DialogHeader>

        <div className="no-scrollbar -mx-6 max-h-[50vh] overflow-y-auto px-6">
          {loaderData.data.timeouts.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {m["lesadministratie.timeouts_empty"]()}
            </p>
          ) : (
            <DataTable
              size="small"
              aria-label={m["lesadministratie.timeouts_aria"]()}
              className="border-0"
              columns={columns}
              data={loaderData.data.timeouts}
              getRowId={(row) => row.lesId}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LessonAdministrationTimeoutsError({ error }: { error: unknown }) {
  const params = Route.useParams();
  return <ErrorBoundaryComponent error={error} params={params} />;
}
